import React, { useEffect, useState } from 'react';
import { mapItemsApi } from '../lib/api';
import EmergencyMap from '../components/map/EmergencyMap';
import { Button } from '../components/ui/button';
import { Printer, ArrowLeft, Radio, Ban, Construction, PartyPopper, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const TYPE_META = {
  road_closure: { label: 'Road Closures', color: '#EF4444', Icon: Ban },
  detour: { label: 'Detours', color: '#F97316', Icon: Construction },
  parade_route: { label: 'Parade Routes', color: '#3B82F6', Icon: PartyPopper },
  closed_area: { label: 'Closed Areas', color: '#EF4444', Icon: AlertTriangle },
};

const TYPE_ORDER = ['road_closure', 'detour', 'parade_route', 'closed_area'];

const formatCoord = (coords, radius) => {
  if (!coords || coords.length === 0) return '—';
  const fmt = (c) => `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
  if (radius && radius > 0) {
    const r = radius >= 1000 ? `${(radius / 1000).toFixed(2)} km` : `${Math.round(radius)} m`;
    return `Center ${fmt(coords[0])} · Radius ${r}`;
  }
  if (coords.length === 1) return fmt(coords[0]);
  return `${fmt(coords[0])} → ${fmt(coords[coords.length - 1])} (${coords.length} points)`;
};

const MapPrintView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState(TYPE_ORDER);
  const generatedAt = new Date();

  useEffect(() => {
    (async () => {
      try {
        const res = await mapItemsApi.getActive();
        setItems(res.data || []);
      } catch (e) {
        console.error('Failed to load map items for print', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const filteredItems = items.filter((i) => selectedTypes.includes(i.item_type));

  const grouped = TYPE_ORDER.filter((type) => selectedTypes.includes(type)).map((type) => ({
    type,
    meta: TYPE_META[type],
    items: filteredItems.filter((i) => i.item_type === type),
  }));

  const dateStr = generatedAt.toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="print-report min-h-screen bg-white text-black" data-testid="map-print-view">
      {/* Screen-only toolbar */}
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 bg-zinc-900 px-4 py-3 text-white">
        <Link to="/map">
          <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white" data-testid="print-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Map
          </Button>
        </Link>
        <div className="text-sm text-zinc-400 hidden sm:block">
          Review the report below, then print or save as PDF.
        </div>
        <Button
          onClick={() => window.print()}
          className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase"
          data-testid="trigger-print-btn"
        >
          <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
        </Button>
      </div>

      {/* Screen-only filter bar */}
      <div className="no-print border-b border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="print-filter-bar">
        <div className="mx-auto max-w-[900px] flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mr-1">
            Include in report:
          </span>
          {TYPE_ORDER.map((type) => {
            const active = selectedTypes.includes(type);
            const meta = TYPE_META[type];
            const count = items.filter((i) => i.item_type === type).length;
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                data-testid={`filter-${type}`}
                aria-pressed={active}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? 'border-transparent text-white'
                    : 'border-zinc-300 bg-white text-zinc-500 hover:bg-zinc-100'
                }`}
                style={active ? { backgroundColor: meta.color } : undefined}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ backgroundColor: active ? '#ffffff' : meta.color }}
                />
                {meta.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Printable content */}
      <div className="report-body mx-auto max-w-[900px] px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-black pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-red-500 flex items-center justify-center">
              <Radio className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">Anaconda-Deer Lodge County</h1>
              <p className="text-sm uppercase tracking-widest text-zinc-600">Emergency Services — Map Report</p>
            </div>
          </div>
          <div className="text-right text-xs text-zinc-600">
            <p>Generated</p>
            <p className="font-semibold text-black">{dateStr}</p>
            <p className="mt-1">
              {filteredItems.length} item(s) shown
              {filteredItems.length !== items.length ? ` of ${items.length} active` : ''}
            </p>
          </div>
        </div>

        {/* Map */}
        <div className="map-print-container border border-zinc-300 rounded overflow-hidden" style={{ height: '460px' }}>
          {!loading && (
            <EmergencyMap
              items={filteredItems}
              height="460px"
              showLegend={false}
              interactive={false}
              fitToItems={true}
            />
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3 mb-5 text-xs">
          {TYPE_ORDER.filter((type) => selectedTypes.includes(type)).map((type) => (
            <div key={type} className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: TYPE_META[type].color }} />
              <span>{TYPE_META[type].label}</span>
            </div>
          ))}
        </div>

        {/* Categorized lists */}
        {filteredItems.length === 0 ? (
          <p className="text-center text-zinc-500 py-8">
            {selectedTypes.length === 0
              ? 'Select at least one category above to include in the report.'
              : 'No active items to report for the selected categories.'}
          </p>
        ) : (
          grouped.map(({ type, meta, items: groupItems }) => {
            if (groupItems.length === 0) return null;
            const { Icon } = meta;
            return (
              <div key={type} className="mb-5 break-inside-avoid" data-testid={`print-group-${type}`}>
                <div className="flex items-center gap-2 mb-2" style={{ color: meta.color }}>
                  <Icon className="w-5 h-5" />
                  <h2 className="text-base font-bold uppercase tracking-wide">
                    {meta.label} ({groupItems.length})
                  </h2>
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-400 text-left text-xs uppercase text-zinc-600">
                      <th className="py-1 pr-3 w-8">#</th>
                      <th className="py-1 pr-3">Title / Description</th>
                      <th className="py-1 pr-3 w-[45%]">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupItems.map((item, idx) => (
                      <tr key={item.id} className="border-b border-zinc-200 align-top break-inside-avoid">
                        <td className="py-2 pr-3 text-zinc-500">{idx + 1}</td>
                        <td className="py-2 pr-3">
                          <div className="font-semibold">{item.title}</div>
                          {item.description && (
                            <div className="text-zinc-600 text-xs mt-0.5">{item.description}</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs text-zinc-700">
                          {formatCoord(item.coordinates, item.radius)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })
        )}

        {/* Footer */}
        <div className="mt-8 pt-3 border-t border-zinc-300 text-[10px] text-zinc-500 flex justify-between">
          <span>Anaconda-Deer Lodge County Emergency Services</span>
          <span>Official use — information current as of generation time</span>
        </div>
      </div>
    </div>
  );
};

export default MapPrintView;
